import { fetchVariableCatalog } from 'api/services/organizationVariableService';
import { useVariableCatalogStore } from 'stores/variableCatalogStore';
import { runWhenIdle } from 'utils/runWhenIdle';

// The catalog is org-scoped: one response serves every surface, so the store is the only cache
// and one in-flight request is shared by every caller. Both survive across mounts for the
// session, so reopening an editor or a template modal does not re-request.
let inFlight: Promise<void> | undefined;
let tenantGeneration = 0;
let schemaRetries = 0;
let schemaRetryTimer: number | undefined;
let cancelIdlePrefetch: (() => void) | undefined;
const SCHEMA_RETRY_LIMIT = 3;
const SCHEMA_RETRY_MS = 20_000;

const clearSchemaRetry = (): void => {
  if (schemaRetryTimer !== undefined) {
    window.clearTimeout(schemaRetryTimer);
    schemaRetryTimer = undefined;
  }
};

/** Test-only: forget the shared in-flight request between Vitest cases. */
export const __resetVariableCatalogCacheForTests = (): void => {
  tenantGeneration += 1;
  clearSchemaRetry();
  cancelIdlePrefetch?.();
  cancelIdlePrefetch = undefined;
  inFlight = undefined;
  schemaRetries = 0;
};

/**
 * Fetch the org catalog and apply it to the store, sharing one request across callers.
 *
 * A request superseded by an invalidation or tenant change is dropped when it lands, so a
 * stale response can never overwrite a newer one or leak one org's names into another.
 */
const loadVariableCatalog = (): Promise<void> => {
  if (inFlight) {
    return inFlight;
  }
  const generation = tenantGeneration;
  const isCurrent = () =>
    generation === tenantGeneration && inFlight === request;
  const request: Promise<void> = fetchVariableCatalog()
    .then((catalog) => {
      if (!isCurrent()) {
        return;
      }
      useVariableCatalogStore
        .getState()
        .applyCatalog(catalog.variables, catalog.deprecated_variables);
      // Store listeners can synchronously switch tenant while applying the response.
      if (!isCurrent()) {
        return;
      }
      if (catalog.job_schema_complete !== false) {
        schemaRetries = 0;
        return;
      }
      // The first response can be the base schema while definitions warm up.
      // A few delayed loads pick up the stored fields without pinning the session.
      if (schemaRetries >= SCHEMA_RETRY_LIMIT) {
        return;
      }
      schemaRetries += 1;
      const timer = window.setTimeout(() => {
        if (generation !== tenantGeneration || schemaRetryTimer !== timer) {
          return;
        }
        schemaRetryTimer = undefined;
        void refreshVariableCatalog().catch(() => {
          // swallow-ok: applyCatalog already stored the base catalog above, so a failed schema retry leaves the base schema in place; it must not reject with nobody listening.
        });
      }, SCHEMA_RETRY_MS);
      schemaRetryTimer = timer;
    })
    .catch((error: unknown) => {
      // A superseded failure is no longer this tenant's load failure.
      // swallow-ok: invalidation/tenant change owns the replacement load; stale failures cannot affect its callers.
      if (!isCurrent()) return;
      throw error;
    })
    .finally(() => {
      if (inFlight === request) {
        inFlight = undefined;
      }
    });
  inFlight = request;
  return request;
};

/**
 * Load the catalog unless the store already holds it. Catalog UX is optional, so a failed
 * fetch leaves the store unfetched and the next caller retries.
 */
export const ensureVariableCatalog = async (): Promise<void> => {
  if (useVariableCatalogStore.getState().fetched) {
    return;
  }
  const generation = tenantGeneration;
  try {
    await loadVariableCatalog();
  } catch (error: unknown) {
    if (generation !== tenantGeneration) return;
    // swallow-ok: the store stays unfetched, which catalog-gated editors read as not loaded (catalogLoaded), so they hold unknown-name warnings instead of flagging false unknowns; the next useEnsureVariableCatalog mount or refreshVariableCatalog retries, and AllVariablesManager reports its own load failure.
    console.warn('[VARIABLE_CATALOG] Failed to load variable catalog:', error);
  }
};

/** Load the catalog for operations whose validation cannot use an unknown allowlist. */
export const requireVariableCatalog = async (): Promise<void> => {
  if (useVariableCatalogStore.getState().fetched) return;
  const generation = tenantGeneration;
  await loadVariableCatalog();
  if (
    generation !== tenantGeneration ||
    !useVariableCatalogStore.getState().fetched
  ) {
    throw new Error('Variable catalog load was superseded');
  }
};

/** Re-fetch the org catalog even when one is loaded, e.g. after a custom-variable edit. */
export const refreshVariableCatalog = (): Promise<void> => {
  clearSchemaRetry();
  inFlight = undefined;
  return loadVariableCatalog();
};

/**
 * Mark the catalog stale so every mounted `useEnsureVariableCatalog` re-fetches it. The old
 * names stay visible until the new response lands. Used after lookup-table mutations.
 */
export const invalidateVariableCatalog = (): void => {
  clearSchemaRetry();
  const hadInFlight = inFlight !== undefined;
  inFlight = undefined;
  useVariableCatalogStore.getState().invalidateCatalog();
  // A load already in flight predates the mutation and will be dropped. `fetched` was
  // already false, so mounted hooks see no change and would not re-fetch on their own.
  if (hadInFlight) {
    void ensureVariableCatalog();
  }
};

/**
 * Drop the cached catalog AND every name it populated, for identity changes.
 *
 * Unlike {@link invalidateVariableCatalog} (same org, so the old names stay usable
 * while the refetch lands), a new tenant must never read the previous org's names —
 * autocomplete does not gate on `fetched`.
 */
export const clearVariableCatalogForTenantChange = (): void => {
  tenantGeneration += 1;
  clearSchemaRetry();
  cancelIdlePrefetch?.();
  cancelIdlePrefetch = undefined;
  schemaRetries = 0;
  inFlight = undefined;
  useVariableCatalogStore.getState().clearCatalog();
};

/**
 * Warm the catalog after sign-in, once the page is idle, so the first editor or template
 * modal opens with autocomplete ready instead of waiting on the request.
 */
export const prefetchVariableCatalog = (): void => {
  cancelIdlePrefetch?.();
  const generation = tenantGeneration;
  cancelIdlePrefetch = runWhenIdle(
    () => {
      if (generation !== tenantGeneration) return;
      cancelIdlePrefetch = undefined;
      void ensureVariableCatalog();
    },
    { idleTimeoutMs: 5000, fallbackDelayMs: 1000 }
  );
};
